import React, { useState } from "react";
import { Sparkles, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import AffiliateCard from "./common/AffiliateCard";
import {
  AFFILIATE_LINKS,
  ARC_PRODUCTS,
  TOOLS_CATEGORIES,
  FTC_DISCLOSURE,
} from "../config/affiliateLinks";

/**
 * ToolsTab — dedicated "Tools & Resources" page.
 *
 * Layout (top to bottom):
 *   1. ARC Premium feature row (Apex + Galaxy) — featured cards, big
 *   2. External affiliate categories — Exchanges, Wallets, DeFi, Security
 *   3. Advanced Trading section — collapsible, gated by user toggle
 *   4. Bonus / Productivity (Wispr Flow at the end per Gven's call)
 *   5. FTC affiliate disclosure footer
 */
const ToolsTab = () => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const advancedLinks = AFFILIATE_LINKS.filter(
    (l) => !l.skip && l.category === "advanced"
  );
  const bonusLinks = AFFILIATE_LINKS.filter(
    (l) => !l.skip && l.category === "bonus"
  );

  return (
    <div className="space-y-6">
      {/* ─── Page header ────────────────────────────────────────────── */}
      <div className="surface-xl p-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles size={16} className="text-arc-gold" />
          <p className="eyebrow">Tools & Resources</p>
        </div>
        <h2 className="headline text-2xl mb-2">
          Swami's curated stack
        </h2>
        <p className="text-arc-muted text-sm leading-relaxed max-w-2xl">
          The exchanges, wallets, security tools, and education programs Swami
          actually uses and recommends. Each link is tracked so we can see what
          actually helps you — and a few of them earn ARCrypto a small commission
          (see disclosure below).
        </p>
      </div>

      {/* ─── ARC Premium products — featured row ───────────────────── */}
      <div className="surface-xl p-6">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="eyebrow mb-1">Work directly with ARC</p>
            <h3 className="headline text-xl">Premium programs</h3>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <AffiliateCard
            link={ARC_PRODUCTS.apexone}
            medium="tools-tab-apex-featured"
            featured
          />
          <AffiliateCard
            link={ARC_PRODUCTS.galaxy}
            medium="tools-tab-galaxy-featured"
            featured
          />
        </div>
      </div>

      {/* ─── External affiliate categories ─────────────────────────── */}
      {TOOLS_CATEGORIES.map((cat) => {
        const links = AFFILIATE_LINKS.filter(
          (l) => !l.skip && l.category === cat.key
        );
        if (links.length === 0) return null;
        return (
          <div key={cat.key} className="surface-xl p-6">
            <div className="mb-4">
              <p className="eyebrow mb-1">{cat.label}</p>
              <p className="text-sm text-arc-muted">{cat.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {links.map((l) => (
                <AffiliateCard
                  key={l.id}
                  link={l}
                  medium={`tools-tab-${cat.key}`}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* ─── Advanced Trading — collapsible, gated section ──────────── */}
      {advancedLinks.length > 0 && (
        <div className="surface-xl p-6">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            className="btn-tactile w-full flex items-center justify-between gap-3 text-left"
          >
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle size={16} className="text-amber-400" />
              </div>
              <div>
                <p className="eyebrow text-amber-400 mb-1">Advanced Trading</p>
                <p className="text-sm text-arc-muted">
                  Leveraged perps platforms — high risk, experienced traders only
                </p>
              </div>
            </div>
            {showAdvanced ? (
              <ChevronUp size={18} className="text-arc-muted flex-shrink-0" />
            ) : (
              <ChevronDown size={18} className="text-arc-muted flex-shrink-0" />
            )}
          </button>

          {showAdvanced && (
            <div className="mt-4 pt-4 border-t border-arc-line">
              <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 mb-4">
                <p className="text-xs text-amber-200 leading-relaxed">
                  <span className="font-semibold">⚠ Leverage warning:</span> these
                  platforms let you trade with borrowed money. You can lose more
                  than you put in. Only use these if you understand liquidation
                  risk, position sizing, and have already built a stable base of
                  spot crypto holdings.
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {advancedLinks.map((l) => (
                  <AffiliateCard
                    key={l.id}
                    link={l}
                    medium="tools-tab-advanced"
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Bonus / Productivity (Wispr Flow at the end) ───────────── */}
      {bonusLinks.length > 0 && (
        <div className="surface-xl p-6">
          <div className="mb-4">
            <p className="eyebrow mb-1">Productivity bonus</p>
            <p className="text-sm text-arc-muted">
              Not finance — just useful tools from Swami's stack
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {bonusLinks.map((l) => (
              <AffiliateCard
                key={l.id}
                link={l}
                medium="tools-tab-bonus"
              />
            ))}
          </div>
        </div>
      )}

      {/* ─── FTC Disclosure ────────────────────────────────────────── */}
      <div className="rounded-xl border border-arc-line bg-white/[0.02] p-4">
        <p className="text-[11px] text-arc-dim leading-relaxed">
          <span className="font-semibold text-arc-muted">Disclosure: </span>
          {FTC_DISCLOSURE}
        </p>
      </div>
    </div>
  );
};

export default ToolsTab;
